import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTheme } from "../../contexts/ThemeContext";
import ApiService from "../../services/api";
import { ReportPreviewModal } from "../InspectionBoard/modals/ReportPreviewModal";

interface InspectionDetailsData {
  id: number;
  vehicle_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  sheets: SheetDetail[];
  vehicle?: {
    number: string;
    tank: string;
    plate: string;
  };
}

interface SheetDetail {
  id: number;
  type: string;
  template_name: string;
  template_id: number;
  status: string;
  items?: ItemDetail[];
}

interface ItemDetail {
  id: number;
  section_name: string;
  item_name: string;
  response?: string;
  notes?: string;
}

const InspectionDetails: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [inspection, setInspection] = useState<InspectionDetailsData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);

  // Load inspection details
  useEffect(() => {
    const loadInspectionDetails = async () => {
      try {
        setLoading(true);
        const data = await ApiService.get<InspectionDetailsData>(
          `/inspections/${id}`
        );

        // Cargar items para cada sheet
        if (data.sheets) {
          const sheetsWithItems = await Promise.all(
            data.sheets.map(async (sheet) => {
              try {
                const [itemsResponse, responsesResponse] = await Promise.all([
                  ApiService.get<any[]>(
                    `/inspections/templates/${sheet.template_id}/items`
                  ),
                  ApiService.get<{ responses: any[] }>(
                    `/inspections/sheets/${sheet.id}/responses`
                  ),
                ]);

                const itemsWithResponses = itemsResponse.map((item: any) => {
                  const responseData = responsesResponse.responses?.find(
                    (r: any) => r.item_id === item.id
                  );
                  return {
                    ...item,
                    response: responseData?.response,
                    notes: responseData?.notes,
                  };
                });

                return {
                  ...sheet,
                  items: itemsWithResponses,
                };
              } catch (error) {
                console.error("Error loading sheet items:", error);
                return sheet;
              }
            })
          );
          data.sheets = sheetsWithItems;
        }

        setInspection(data);
      } catch (error) {
        console.error("Error loading inspection:", error);
        setError("Failed to load inspection details");
      } finally {
        setLoading(false);
      }
    };

    loadInspectionDetails();
  }, [id]);

  const downloadReport = async () => {
    if (!inspection) return;

    setDownloading(true);
    try {
      // Llamar al endpoint del backend para descargar PDF
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1"
        }/inspections/${inspection.id}/report/pdf`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download report");
      }

      // Obtener el blob del PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `inspection_${inspection.id}_report_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar("Report downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading report:", error);
      enqueueSnackbar("Failed to download report", { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const viewReport = () => {
    if (!inspection) return;
    setShowReportPreview(true);
  };

  const closeReportPreview = () => {
    setShowReportPreview(false);
  };

  const getResponseColor = (response?: string): string => {
    if (!response) return "bg-gray-200 text-gray-700";
    if (response === "pass") return "bg-green-100 text-green-800";
    if (response === "fail") return "bg-red-100 text-red-800";
    if (response === "na") return "bg-gray-100 text-gray-800";
    if (response === "checked") return "bg-blue-100 text-blue-800";
    return "bg-gray-200 text-gray-700";
  };

  const getResponseText = (response?: string): string => {
    if (!response) return "No Response";
    if (response === "pass") return "Pass";
    if (response === "fail") return "Fail";
    if (response === "na") return "N/A";
    if (response === "checked") return "✓ Checked";
    return response;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
            isDarkMode ? "border-orange-400" : "border-blue-600"
          }`}
        ></div>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3
            className={`mt-4 text-lg font-medium ${
              isDarkMode ? "text-gray-100" : "text-gray-900"
            }`}
          >
            Error
          </h3>
          <p
            className={`mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {error || "Inspection not found"}
          </p>
          <button
            onClick={() => navigate("/inspections")}
            className={`mt-4 px-4 py-2 rounded-md ${
              isDarkMode
                ? "bg-orange-600 text-white hover:bg-orange-700"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  const totalItems = inspection.sheets.reduce(
    (acc, sheet) => acc + (sheet.items?.length || 0),
    0
  );
  const passedItems = inspection.sheets.reduce(
    (acc, sheet) =>
      acc + (sheet.items?.filter((i) => i.response === "pass").length || 0),
    0
  );
  const failedItems = inspection.sheets.reduce(
    (acc, sheet) =>
      acc + (sheet.items?.filter((i) => i.response === "fail").length || 0),
    0
  );
  const naItems = inspection.sheets.reduce(
    (acc, sheet) =>
      acc + (sheet.items?.filter((i) => i.response === "na").length || 0),
    0
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/inspections")}
                className={
                  isDarkMode ? "mr-4 text-gray-300" : "mr-4 text-black"
                }
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-gray-100" : "text-black"
                  }`}
                >
                  Inspection #{inspection.id}
                </h1>
                <p
                  className={`mt-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-900"
                  }`}
                >
                  Vehicle{" "}
                  {inspection.vehicle?.number || `ID ${inspection.vehicle_id}`}
                  {inspection.vehicle?.tank && ` • ${inspection.vehicle.tank}`}
                  {inspection.vehicle?.plate &&
                    ` • ${inspection.vehicle.plate}`}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full whitespace-nowrap ${
                  inspection.status === "open"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {inspection.status.toUpperCase()}
              </span>
              <button
                onClick={viewReport}
                disabled={downloading}
                className={`px-4 py-2 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                  isDarkMode
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                {downloading ? "Loading..." : "View Report"}
              </button>
              <button
                onClick={downloadReport}
                disabled={downloading}
                className={`px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                  isDarkMode
                    ? "bg-gray-700 text-purple-400 hover:bg-gray-600"
                    : "bg-white text-purple-600 hover:bg-purple-50"
                }`}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {downloading ? "Downloading..." : "Download"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
          <div
            className={`rounded-lg shadow p-4 sm:p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <p
              className={`text-xs sm:text-sm font-medium ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Total Items
            </p>
            <p
              className={`text-2xl sm:text-3xl font-bold mt-1 sm:mt-2 ${
                isDarkMode ? "text-purple-400" : "text-purple-600"
              }`}
            >
              {totalItems}
            </p>
          </div>
          <div
            className={`rounded-lg shadow p-4 sm:p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <p
              className={`text-xs sm:text-sm font-medium ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Passed
            </p>
            <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2 text-green-600">
              {passedItems}
            </p>
          </div>
          <div
            className={`rounded-lg shadow p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <p
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Failed
            </p>
            <p className="text-3xl font-bold mt-2 text-red-600">
              {failedItems}
            </p>
          </div>
          <div
            className={`rounded-lg shadow p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <p
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              N/A
            </p>
            <p
              className={`text-3xl font-bold mt-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {naItems}
            </p>
          </div>
        </div>

        {/* Inspection Info */}
        <div
          className={`rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <h2
            className={`text-base sm:text-lg font-semibold mb-4 ${
              isDarkMode ? "text-gray-100" : "text-gray-900"
            }`}
          >
            Inspection Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Created
              </p>
              <p
                className={`text-base font-medium ${
                  isDarkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {new Date(inspection.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Last Updated
              </p>
              <p
                className={`text-base font-medium ${
                  isDarkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {new Date(inspection.updated_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Total Sheets
              </p>
              <p
                className={`text-base font-medium ${
                  isDarkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {inspection.sheets.length}
              </p>
            </div>
            <div>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Completed Sheets
              </p>
              <p className="text-base font-medium text-gray-900">
                {
                  inspection.sheets.filter((s) => s.status === "completed")
                    .length
                }
              </p>
            </div>
          </div>
        </div>

        {/* Sheets Details */}
        {inspection.sheets.map((sheet) => {
          const itemsBySection: Record<string, ItemDetail[]> = {};
          sheet.items?.forEach((item) => {
            if (!itemsBySection[item.section_name]) {
              itemsBySection[item.section_name] = [];
            }
            itemsBySection[item.section_name].push(item);
          });

          return (
            <div
              key={sheet.id}
              className={`rounded-lg shadow mb-4 sm:mb-6 ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div
                className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div>
                  <h2
                    className={`text-base sm:text-lg font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {sheet.template_name.replace(/_/g, " ")}
                  </h2>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {sheet.type.replace(/_/g, " ")}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 text-sm font-semibold rounded-full ${
                    sheet.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {sheet.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="p-4 sm:p-6">
                {Object.entries(itemsBySection).map(([sectionName, items]) => (
                  <div key={sectionName} className="mb-6 last:mb-0">
                    <h3
                      className={`text-md font-semibold mb-3 pb-2 border-b ${
                        isDarkMode
                          ? "text-gray-200 border-gray-700"
                          : "text-gray-800 border-gray-200"
                      }`}
                    >
                      {sectionName}
                    </h3>
                    <div className="space-y-2">
                      {items.map((item) => (
                        <div
                          key={item.id}
                          className={`p-3 rounded-lg ${
                            isDarkMode ? "bg-gray-700" : "bg-gray-50"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span
                              className={`text-sm ${
                                isDarkMode ? "text-gray-200" : "text-gray-900"
                              }`}
                            >
                              {item.item_name}
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${getResponseColor(
                                item.response
                              )}`}
                            >
                              {getResponseText(item.response)}
                            </span>
                          </div>
                          {item.notes && (
                            <div
                              className={`mt-2 pt-2 border-t text-sm italic ${
                                isDarkMode
                                  ? "border-gray-600 text-gray-400"
                                  : "border-gray-200 text-gray-600"
                              }`}
                            >
                              <span className="font-medium not-italic">
                                Note:{" "}
                              </span>
                              {item.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de reportes con tabs */}
      <ReportPreviewModal
        show={showReportPreview}
        isDarkMode={isDarkMode}
        inspectionId={inspection?.id || 0}
        vehicleNumber={inspection?.vehicle?.number || "N/A"}
        reportUrl={`${
          process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1"
        }/inspections/${inspection?.id}/report/pdf`}
        onClose={closeReportPreview}
      />
    </div>
  );
};

export default InspectionDetails;
